/**
 * Members view — invite + manage org users.
 *
 * Renders WorkOS's `<UsersManagement />` widget inside `<WorkOsWidgets>`.
 * The widget owns its own panel surface (Radix Theme), so we deliberately
 * skip our `.card` wrapper to avoid double-framing. We just give it room.
 *
 * Inviting goes through the SAME pipeline as Workforce → Add user
 * (`useAddSiteUser`: minetwin add_user → org project grant → our
 * `/api/invite-user` Function), not the widget's built-in "Invite user"
 * button (hidden via `.mt-members-widget` in styles.css). Two reasons:
 *   - The widget always sends a WorkOS invitation, which a user whose email
 *     domain is SSO-bound to another org can never accept (AuthKit stalls
 *     silently). The Function adds existing WorkOS users directly and
 *     invites only new emails.
 *   - A WorkOS-only invite creates a login and NO roster row, so
 *     coordinators who came here first had to "add" the same person again
 *     on Workforce. Now an invite from here lands them on the current
 *     site's roster with their occupation in one step, and "Invite
 *     several" takes a pasted list.
 */

import { Suspense, useState } from 'react';
import {
  WorkOsWidgets,
  UsersManagement,
  UsersManagementLoading,
} from '@workos-inc/widgets';
// Radix Themes CSS (the underlying design system the widget is built on).
// Must come BEFORE the widget's own overrides.
import '@radix-ui/themes/styles.css';
// The widget package's `exports` field doesn't expose its CSS at all, so we
// route via the Vite alias `@workos-inc/widgets/css` → its `dist/css/` dir.
// Without these imports the widget renders as raw unstyled HTML.
import '@workos-inc/widgets/css/base.css';
import '@workos-inc/widgets/css/styles.css';
import '@workos-inc/widgets/css/users-management.css';
import { useAuthKit } from '../../contexts/AuthProvider';
import { useTweaks } from '../../hooks/useTweaks';
import { useProjects } from '../../hooks/useProjects';
import { useUsers } from '../../hooks/useUsers';
import { useWorkosRoster } from '../../hooks/useWorkosRoster';
import { useOccupations } from '../../hooks/useOccupations';
import { useAddSiteUser, type AddSiteUserInput } from '../../hooks/useAddSiteUser';
import { useIsOrgAdmin } from '../../hooks/useIsOrgAdmin';
import { useSiteAccess } from '../../hooks/useSiteAccess';
import { AddUserModal } from '../Workforce/AddUserModal';
import { BulkAddUsersModal } from '../Workforce/BulkAddUsersModal';
import type { RowOutcome } from '../../lib/bulkAddUsers';
import type { Site, Toast } from '../../data/types';

type MembersProps = { toast: (t: Omit<Toast, 'id'>) => void };

type InviteState = 'none' | 'one' | 'several';

export function Members({ toast }: MembersProps) {
  const { getAccessToken } = useAuthKit();
  const { tweaks } = useTweaks();
  const { sites } = useProjects();
  const { users } = useUsers();
  const workosRoster = useWorkosRoster();
  // Pre-warm the occupation catalog so the drawer opens with the dropdown
  // populated (same trick as Workforce).
  useOccupations();
  // The site an invitee joins = the one picked in the topbar, exactly as
  // on Workforce. No site → the drawer says so and refuses to submit.
  const currentSite: Site | undefined = sites[tweaks.siteIndex] ?? sites[0];
  const defaultProjectId = currentSite?.projectName ?? '';
  const { addOneUser } = useAddSiteUser(currentSite);
  // Gates the site Admin role and the org-admin checkbox (#241).
  const viewerIsOrgAdmin = useIsOrgAdmin();
  // Invite is the site's Admin or the TurboTwin org admin only (D9).
  const access = useSiteAccess(defaultProjectId);
  const [invite, setInvite] = useState<InviteState>('none');
  const [adding, setAdding] = useState(false);
  // Bumped after an invite so the widget remounts and refetches its list
  // (its query cache is internal; there is no refresh prop).
  const [widgetKey, setWidgetKey] = useState(0);
  // WorkOS Radix theme takes 'light' | 'dark' | 'inherit'. Mirror the app's
  // selected theme so the embedded UsersManagement widget doesn't stay dark
  // when the rest of the portal flips to light mode.
  const widgetAppearance = tweaks.theme === 'light' ? 'light' : 'dark';

  return (
    <>
      <div className="page-head">
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>Members</h1>
          <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 4 }}>
            Invite and manage users for this organization. An invite also puts
            the person on the <b>{currentSite?.name ?? 'selected site'}</b>{' '}
            roster with their occupation — the same as Workforce → Add user.
          </div>
        </div>
        {access.manageRoles ? (
        <div className="page-actions">
          <button
            type="button"
            className="btn"
            disabled={adding}
            onClick={() => setInvite('several')}
          >
            Invite several
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={adding}
            onClick={() => setInvite('one')}
          >
            Invite user
          </button>
        </div>
        ) : null}
      </div>

      <div className="page-body">
        <div
          style={{
            // Constrain to a comfortable reading width on wide displays but
            // still wide enough for the role + status columns.
            maxWidth: 1100,
            // Reserve vertical space so the page doesn't collapse during
            // auth-token fetch / first API call.
            minHeight: 600,
          }}
        >
          <WorkOsWidgets
            theme={{
              appearance: widgetAppearance,
              accentColor: 'orange',
              grayColor: 'slate',
              panelBackground: 'solid',
              radius: 'medium',
              scaling: '100%',
              fontFamily:
                "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            <Suspense fallback={<UsersManagementLoading className="mt-members-widget" />}>
              <UsersManagement
                key={widgetKey}
                className="mt-members-widget"
                authToken={() => getAccessToken()}
              />
            </Suspense>
          </WorkOsWidgets>
        </div>
      </div>

      {invite === 'one' ? (
        <AddUserModal
          title="Invite user"
          submitLabel="Invite"
          allowOrgAdmin
          viewerIsOrgAdmin={viewerIsOrgAdmin}
          viewerIsSiteAdmin={access.siteAdmin}
          defaultProjectId={defaultProjectId}
          busy={adding}
          onCancel={() => setInvite('none')}
          onSubmit={(input) => void handleInvite(input)}
          onSwitchToBulk={() => setInvite('several')}
          users={users}
          workosRoster={workosRoster}
        />
      ) : null}
      {invite === 'several' ? (
        <BulkAddUsersModal
          projectId={defaultProjectId}
          siteName={currentSite?.name ?? defaultProjectId}
          users={users}
          viewerIsOrgAdmin={viewerIsOrgAdmin}
          onCancel={() => setInvite('none')}
          addOne={addOneUser}
          onFinished={(summary) => {
            workosRoster.refresh();
            setWidgetKey((k) => k + 1);
            toast(summary);
          }}
        />
      ) : null}
    </>
  );

  async function handleInvite(input: AddSiteUserInput) {
    if (adding) return;
    setAdding(true);
    let outcome: RowOutcome;
    try {
      outcome = await addOneUser(input);
    } finally {
      setAdding(false);
    }
    if (outcome.kind === 'failed') {
      toast({ title: 'Invite failed', sub: outcome.note, kind: 'bad' });
      return;
    }
    setInvite('none');
    workosRoster.refresh();
    setWidgetKey((k) => k + 1);
    toast({
      title: outcome.kind === 'invited' ? 'User invited' : 'User added',
      sub: outcome.note,
      kind: outcome.kind === 'invite-failed' ? 'warn' : 'good',
    });
  }
}
